<template>
    <el-dialog label-width="120px" label-position="top" :fullscreen="true" :visible="display"
               @closed="closed" @close="$emit('close', false)" @opened="opened" :destroy-on-close="true">
        <slot name="title">
            <h3 class="text-center">{{ $t(`department.title.${edit ? 'update' : 'create'}`) }}</h3>
            <hr>
        </slot>

        <div style="max-height: 80vh; overflow-y: auto;">
          <el-row>
            <el-col :span="20" :offset="2">
              <el-form :model="form" size="medium" :rules="rules" ref="deptForm" label-width="120px">

                <!-- Basic Information -->
                <el-form-item label="Department name" prop="name">
                  <el-input @keyup.enter.native="submitForm" ref="input" v-model="form.name" autocomplete="off"></el-input>
                </el-form-item>

                <el-form-item label="Address" prop="address">
                  <el-input v-model="form.address" placeholder="Enter department address"></el-input>
                </el-form-item>

                <el-form-item label="Phone">
                  <el-input v-model="form.phone" placeholder="Enter phone number"></el-input>
                </el-form-item>

                <el-form-item label="Description">
                  <el-input type="textarea" v-model="form.description" :rows="3" placeholder="Enter description"></el-input>
                </el-form-item>

                <!-- Address Information -->
                <el-divider content-position="left">
                  <i class="el-icon-location"></i>
                  Location Details
                </el-divider>

                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="Country" prop="country_id">
                      <el-select v-model="form.country_id" placeholder="Select country" style="width: 100%" @change="onCountryChange">
                        <el-option
                          v-for="country in countries"
                          :key="country.id"
                          :label="country.name"
                          :value="country.id"
                        >
                          <span style="float: left">{{ country.name }}</span>
                          <span style="float: right; color: #8492a6; font-size: 13px">{{ country.code }}</span>
                        </el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="Country Name">
                      <el-input v-model="form.country" placeholder="Country name"></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>

                <!-- Map and Coordinates -->
                <el-form-item label="Store Location">
                  <div style="margin-bottom: 10px;">
                    <small style="color: #606266;">Click on the map to select the store location</small>
                  </div>

                  <LocationPicker
                    v-model="locationData"
                    :initial-center="mapCenter"
                    :zoom="mapZoom"
                    @change="onLocationChange"
                    @error="onMapError"
                    ref="locationPicker"
                  />
                </el-form-item>

                <!-- Manual Coordinate Entry -->
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="Latitude" prop="latitude">
                      <el-input-number
                        v-model="form.latitude"
                        :precision="6"
                        :step="0.000001"
                        :min="-90"
                        :max="90"
                        style="width: 100%"
                        @change="onCoordinateChange"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="Longitude" prop="longitude">
                      <el-input-number
                        v-model="form.longitude"
                        :precision="6"
                        :step="0.000001"
                        :min="-180"
                        :max="180"
                        style="width: 100%"
                        @change="onCoordinateChange"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

              </el-form>
            </el-col>
          </el-row>
        </div>

        <el-row slot="footer" class="dialog-footer">
            <el-col :span="12" :offset="6">
                <el-button @click="$emit('close', false)">Cancel</el-button>
                <el-button :disabled="!submittable" :loading="loading" type="primary" @click="submitForm">
                    {{ $t(`department.button.${edit ? 'update' : 'create'}`) }}
                </el-button>
            </el-col>
        </el-row>

    </el-dialog>
</template>

<script>
import LocationPicker from '@/components/_Partials/LocationPicker.vue'

export default {
  name: 'CreateDepartment',
  components: {
    LocationPicker
  },
  props: {
    display: {
      type: Boolean,
      required: true
    },
    department: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data () {
    return {
      countries: [],
      locationData: { latitude: null, longitude: null },
      mapCenter: { lat: 4.0511, lng: 9.7679 }, // Default to Cameroon
      mapZoom: 10,
      form: {
        name: this.department.name || '',
        address: this.department.address || '',
        phone: this.department.phone || '',
        description: this.department.description || '',
        latitude: this.department.latitude || null,
        longitude: this.department.longitude || null,
        country: this.department.country || '',
        country_id: this.department.country_id || null
      },
      rules: {
        name: [
          { required: true, message: 'Please, enter the department name', trigger: 'blur' }
        ],
        address: [
          { required: true, message: 'Please, enter the department address', trigger: 'blur' }
        ],
        latitude: [
          {
            validator: (rule, value, callback) => {
              if (value !== null && value !== '' && (value < -90 || value > 90)) {
                callback(new Error('Latitude must be between -90 and 90'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        longitude: [
          {
            validator: (rule, value, callback) => {
              if (value !== null && value !== '' && (value < -180 || value > 180)) {
                callback(new Error('Longitude must be between -180 and 180'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      },
      loading: false
    }
  },
  computed: {
    submittable () {
      return !!this.form.name.length
    },
    edit () {
      return this.department && this.department.id
    }
  },
  methods: {
    opened () {
      if (this.department && this.department.id) {
        this.form = {
          name: this.department.name || '',
          address: this.department.address || '',
          phone: this.department.phone || '',
          description: this.department.description || '',
          latitude: this.department.latitude || null,
          longitude: this.department.longitude || null,
          country: this.department.country || '',
          country_id: this.department.country_id || null
        }

        // Set location data for map
        if (this.department.latitude && this.department.longitude) {
          this.locationData = {
            latitude: this.department.latitude,
            longitude: this.department.longitude
          }
        }

        // Set map center based on department location or country
        if (this.department.latitude && this.department.longitude) {
          this.mapCenter = {
            lat: this.department.latitude,
            lng: this.department.longitude
          }
          this.mapZoom = 15
        } else if (this.department.country_id) {
          const selectedCountry = this.countries.find(c => c.id === this.department.country_id)
          if (selectedCountry && selectedCountry.latitude && selectedCountry.longitude) {
            this.mapCenter = {
              lat: selectedCountry.latitude,
              lng: selectedCountry.longitude
            }
            this.mapZoom = 8
          }
        }
      }
      this.$refs.input.focus()
    },

    closed () {
      this.form = {
        name: '',
        address: '',
        phone: '',
        description: '',
        latitude: null,
        longitude: null,
        country: '',
        country_id: null
      }
      this.locationData = { latitude: null, longitude: null }
      this.loading = false
    },

    async submitForm () {
      try {
        await this.withLoading(async () => {
          // Prepare data with address information
          const departmentData = {
            ...this.form,
            latitude: this.form.latitude || this.locationData.latitude,
            longitude: this.form.longitude || this.locationData.longitude
          }

          if (this.department.id) {
            await this.$departmentService.updateDepartment(this.department.id, departmentData)
            this.showSuccess(this.$t('department.message.updated'))
          } else {
            await this.$departmentService.createDepartment(departmentData)
            this.showSuccess(this.$t('department.message.created'))
          }
          this.$emit('updated', departmentData)
          this.$emit('department-ok')
          this.$refs.deptForm.resetFields()
          this.$emit('close', false)
        }, 'loading')
      } catch (error) {
        // Error is already handled by the global interceptor
        console.log('Component caught error in submitForm:', error)
      }
    },

    onLocationChange (coordinates) {
      this.form.latitude = coordinates.latitude
      this.form.longitude = coordinates.longitude
      this.locationData = coordinates
    },

    onCoordinateChange () {
      // Update location picker when coordinates are manually entered
      if (this.form.latitude && this.form.longitude) {
        this.locationData = {
          latitude: this.form.latitude,
          longitude: this.form.longitude
        }
        if (this.$refs.locationPicker) {
          this.$refs.locationPicker.setLocation(this.form.latitude, this.form.longitude)
        }
      }
    },

    onCountryChange (countryId) {
      const selectedCountry = this.countries.find(c => c.id === countryId)
      if (selectedCountry) {
        this.form.country = selectedCountry.name

        // Reposition map to selected country if it has coordinates
        if (selectedCountry.latitude && selectedCountry.longitude) {
          this.mapCenter = {
            lat: selectedCountry.latitude,
            lng: selectedCountry.longitude
          }

          if (this.$refs.locationPicker) {
            this.$refs.locationPicker.centerMap(
              selectedCountry.latitude,
              selectedCountry.longitude,
              8
            )
          }
        }
      }
    },

    onMapError (error) {
      this.$message.warning(error)
    },

    async loadCountries () {
      try {
        this.countries = await this.$countryService.getCountries()
      } catch (error) {
        console.error('Error loading countries:', error)
      }
    },
    showSuccess (message) {
      this.notify(message, this.$t('status.success'))
    },
    withLoading (callback, loadingKey) {
      this.loading = true
      return callback()
        .catch((error) => {
          this.notify(error.response?.data?.message || error.message, this.$t('status.error'), 'error')
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  async mounted () {
    await this.loadCountries()
  }
}
</script>

<style scoped>

</style>
